import { useState, useEffect, useRef, useCallback } from 'react';
import { useChatStore, wsBuilder } from '@entities/messanger';
import { useWebSocketContext } from '@shared/context/WebSocketProvider';

const HISTORY_TIMEOUT = 10000; // 10 секунд на ответ

export const useMessageList = () => {
  const [page, setPage] = useState(0);
  const { sendJsonMessage } = useWebSocketContext();
  const timeoutRef = useRef(null);

  const selectedChat = useChatStore((state) => state.selectedChat);
  const messageHistory = useChatStore((state) => state.messageHistory);
  const isEnd = useChatStore((state) => state.isEnd);
  const historyLoading = useChatStore((state) => state.historyLoading);
  const historyError = useChatStore((state) => state.historyError);
  const setHistoryLoading = useChatStore((state) => state.setHistoryLoading);
  const setHistoryError = useChatStore((state) => state.setHistoryError);
  const selectedChatId = selectedChat?.id;

  // При смене чата сбрасываем страницу и ошибку
  useEffect(() => {
    setPage(0);
    setHistoryError(null);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, [selectedChatId, setHistoryError]);

  // Отправляем запрос истории при изменении page или selectedChat
  useEffect(() => {
    if (!selectedChatId) return;

    setHistoryLoading(true);
    setHistoryError(null);

    sendJsonMessage(wsBuilder.historyRequest(selectedChatId, page));

    // Таймаут: если за 10 секунд нет ответа — считаем ошибкой
    timeoutRef.current = setTimeout(() => {
      const stillLoading = useChatStore.getState().historyLoading;
      if (stillLoading) {
        setHistoryLoading(false);
        setHistoryError('Не удалось загрузить историю сообщений');
      }
    }, HISTORY_TIMEOUT);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [selectedChatId, page, sendJsonMessage, setHistoryLoading, setHistoryError]);

  const fetchMoreData = useCallback(() => {
    if (historyLoading || isEnd || historyError) return;
    setPage((prev) => prev + 1);
  }, [historyLoading, isEnd, historyError]);

  const retryHistory = useCallback(() => {
    if (!selectedChatId) return;
    setPage(0);
    setHistoryError(null);
    setHistoryLoading(true);
    sendJsonMessage(wsBuilder.historyRequest(selectedChatId, 0));
  }, [selectedChatId, sendJsonMessage, setHistoryError, setHistoryLoading]);

  return {
    messageHistory,
    isEnd,
    historyLoading,
    historyError,
    fetchMoreData,
    retryHistory,
    selectedChat,
  };
};
